import React, { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Paperclip, Send, X, UploadCloud, ArrowLeft } from "lucide-react";
import { db, auth, storage } from "@/firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

interface SupportSubmissionProps {
  traceId?: string;
  defaultSubject?: string;
  onClose?: () => void;
  onBack?: () => void;
  hideHeader?: boolean;
}

const SupportSubmission: React.FC<SupportSubmissionProps> = ({ traceId, defaultSubject, onClose, onBack, hideHeader = false }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    schoolName: '',
    role: 'student',
    category: 'bug',
    subject: defaultSubject || '',
    description: '',
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const currentUser = auth.currentUser;
      
      // Upload files to Firebase Storage if any
      const uploadedFileURLs: { name: string; url: string; type: string }[] = [];
      for (const file of files) {
        const storageRef = ref(storage, `support-tickets/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        uploadedFileURLs.push({
          name: file.name,
          url: downloadURL,
          type: file.type
        });
      }

      // Create support ticket in Firestore
      const ticketData = {
        ...formData,
        attachments: uploadedFileURLs,
        status: 'open',
        priority: 'medium',
        submittedBy: currentUser?.uid || 'anonymous',
        submitterEmail: currentUser?.email || formData.email,
        traceId: traceId || `TRACE-${Date.now()}`,
        systemInfo: {
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString(),
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        responses: []
      };

      const docRef = await addDoc(collection(db, 'supportTickets'), ticketData);
      
      console.log('Support ticket created with ID:', docRef.id);

      toast({
        title: "Support Request Sent Successfully! ✓",
        description: `Ticket #${docRef.id.slice(0, 8).toUpperCase()} - We'll get back to you shortly.`,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        schoolName: '',
        role: 'student',
        category: 'bug',
        subject: defaultSubject || '',
        description: '',
      });
      setFiles([]);

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      toast({
        title: "Submission Failed",
        description: "Please try again later or email us directly at support@sonomacloud.com",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "h-11 rounded-2xl border border-slate-200/80 bg-white/80 px-4 text-sm font-medium text-slate-700 shadow-[0_1px_0_rgba(15,23,42,0.04)] transition-all duration-200 placeholder:font-normal placeholder:text-slate-400 hover:border-blue-200 focus:border-blue-500 focus:bg-white dark:border-white/15 dark:bg-white/5 dark:text-slate-100 dark:hover:border-blue-400/60";

  const selectContentClasses = "rounded-2xl border border-slate-200/70 bg-white/95 backdrop-blur-xl shadow-2xl dark:border-white/10 dark:bg-slate-900/95";

  const selectItemClasses = "rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition data-[highlighted]:bg-slate-100 data-[state=checked]:bg-slate-200/70 dark:text-slate-100 dark:data-[highlighted]:bg-slate-800 dark:data-[state=checked]:bg-slate-700";

  return (
    <div className="w-full">
      {!hideHeader && (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Submit a request</h2>
          <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">We'll get back to you as soon as possible.</p>
          {traceId && (
            <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full bg-slate-100 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Ref ID: <span className="font-mono ml-1">{traceId}</span>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs font-medium text-slate-700 dark:text-slate-300">Name</Label>
            <Input 
              id="name" 
              placeholder="Your name" 
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              required 
              className={inputClasses}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-medium text-slate-700 dark:text-slate-300">Email</Label>
            <Input 
              id="email" 
              type="email" 
              placeholder="your@email.com" 
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              required 
              className={inputClasses}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone" className="text-xs font-medium text-slate-700 dark:text-slate-300">Phone Number</Label>
            <Input 
              id="phone" 
              type="tel" 
              placeholder="+254 700 000 000" 
              value={formData.phone}
              onChange={e => setFormData({...formData, phone: e.target.value})}
              className={inputClasses}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="schoolName" className="text-xs font-medium text-slate-700 dark:text-slate-300">School Name</Label>
            <Input 
              id="schoolName" 
              placeholder="Enter school name" 
              value={formData.schoolName}
              onChange={e => setFormData({...formData, schoolName: e.target.value})}
              required 
              className={inputClasses}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="role" className="text-xs font-medium text-slate-700 dark:text-slate-300">Your Role</Label>
            <Select 
              value={formData.role} 
              onValueChange={val => setFormData({...formData, role: val})}
            >
              <SelectTrigger className={inputClasses}>
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent className={selectContentClasses}>
                <SelectItem value="student" className={selectItemClasses}>Student</SelectItem>
                <SelectItem value="school-admin" className={selectItemClasses}>School Admin</SelectItem>
                <SelectItem value="guardian" className={selectItemClasses}>Guardian</SelectItem>
                <SelectItem value="school-staff" className={selectItemClasses}>School Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="category" className="text-xs font-medium text-slate-700 dark:text-slate-300">Issue Type</Label>
            <Select 
              value={formData.category} 
              onValueChange={val => setFormData({...formData, category: val})}
            >
              <SelectTrigger className={inputClasses}>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className={selectContentClasses}>
                <SelectItem value="bug" className={selectItemClasses}>Report a Bug</SelectItem>
                <SelectItem value="feature" className={selectItemClasses}>Feature Request</SelectItem>
                <SelectItem value="billing" className={selectItemClasses}>Billing Issue</SelectItem>
                <SelectItem value="account" className={selectItemClasses}>Account Access</SelectItem>
                <SelectItem value="other" className={selectItemClasses}>Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="subject" className="text-xs font-medium text-slate-700 dark:text-slate-300">Subject</Label>
          <Input 
            id="subject" 
            placeholder="Brief summary of the issue" 
            value={formData.subject}
            onChange={e => setFormData({...formData, subject: e.target.value})}
            required 
            className={inputClasses}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="description" className="text-xs font-medium text-slate-700 dark:text-slate-300">Description</Label>
          <Textarea 
            id="description" 
            placeholder="Please describe what happened..." 
            className="min-h-[120px] rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-3 text-sm text-slate-700 shadow-[0_1px_0_rgba(15,23,42,0.04)] transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:bg-white dark:border-white/15 dark:bg-white/5 dark:text-slate-100"
            value={formData.description}
            onChange={e => setFormData({...formData, description: e.target.value})}
            required 
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">Attachments</Label>
            <span className="text-xs text-slate-400">PNG, JPG, PDF · max 5MB each</span>
          </div>
          <div className="flex items-center gap-3">
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 cursor-pointer rounded-2xl border border-dashed border-slate-300/70 bg-white/80 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:border-blue-300 hover:bg-blue-50/60 dark:border-white/15 dark:bg-white/5 dark:text-slate-200"
            >
              <div className="flex items-center justify-center gap-2">
                <UploadCloud className="h-4 w-4 text-blue-500" />
                Drop files or click to upload
              </div>
            </div>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              multiple 
              accept="image/*,.pdf,.log,.txt" 
              onChange={handleFileChange} 
            />
          </div>

          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((file, i) => (
                <div key={i} className="flex items-center gap-2 rounded-2xl border border-slate-200/80 bg-white/70 px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                  <Paperclip className="w-3 h-3 text-slate-400" />
                  <span className="truncate max-w-[150px]">{file.name}</span>
                  <button
                    type="button"
                    aria-label="Remove file"
                    onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    className="rounded-full p-1 text-slate-400 transition hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={`mt-6 flex items-center ${onBack ? 'justify-between' : 'justify-end'} gap-3 border-t border-slate-100 pt-4 dark:border-white/10`}>
          {onBack && (
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              onClick={onBack} 
              className="border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="mr-2 h-3.5 w-3.5" />
              Back
            </Button>
          )}
          <div className="flex items-center gap-3">
            {onClose && (
              <Button type="button" variant="ghost" size="sm" onClick={onClose} className="text-slate-600 dark:text-slate-400 dark:hover:text-slate-200">
                Cancel
              </Button>
            )}
            <Button 
              type="submit" 
              disabled={isSubmitting} 
              size="sm"
              className="rounded-2xl px-6 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-[0_12px_30px_rgba(59,130,246,0.35)] transition active:scale-[0.99] disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-3.5 w-3.5" />
                  Submit
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SupportSubmission;
